import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { api, API } from '../lib/api';
import GoogleSignInButton from './GoogleSignInButton';
import { Smartphone, KeyRound, Lock, ShieldCheck, ArrowLeft } from 'lucide-react';

// Modes:
//   'home'        – mobile entry + tab: OTP or Password
//   'otp'         – enter OTP after Send OTP
//   'forgot-otp'  – enter OTP for forgot password flow
//   'forgot-set'  – set new password
//   'set-pw'      – post-login optional "set a password" prompt
const LoginModal = ({ open, onOpenChange }) => {
  const { toast } = useToast();
  const [mode, setMode] = useState('home');
  const [tab, setTab] = useState('otp'); // 'otp' | 'password'
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [devOtp, setDevOtp] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [postLoginToken, setPostLoginToken] = useState(null);
  // GDPR / T&C explicit consent — required to enable any sign-in/sign-up
  // action from the home screen of this modal. Persists for the lifetime of
  // the modal (resetAll restores to false).
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const resetAll = () => {
    setMode('home'); setTab('otp'); setMobile(''); setOtp(''); setPassword('');
    setNewPassword(''); setConfirmPassword(''); setDevOtp(null); setPostLoginToken(null);
    setAcceptedTerms(false);
  };
  const close = () => { onOpenChange(false); setTimeout(resetAll, 250); };

  const validateMobile = () => {
    if (!mobile || mobile.length !== 10) {
      toast({ title: 'Enter a valid 10-digit mobile number', variant: 'destructive' });
      return false;
    }
    return true;
  };
  const validateNewPassword = () => {
    if (newPassword.length < 8) { toast({ title: 'Password must be at least 8 characters', variant: 'destructive' }); return false; }
    if (!/[A-Za-z]/.test(newPassword) || !/\d/.test(newPassword)) { toast({ title: 'Password must contain letters and numbers', variant: 'destructive' }); return false; }
    if (newPassword !== confirmPassword) { toast({ title: 'Passwords do not match', variant: 'destructive' }); return false; }
    return true;
  };

  const sendOtp = async (e) => {
    e?.preventDefault?.();
    if (!acceptedTerms) return toast({ title: 'Please accept the Terms & Privacy Policy to continue', variant: 'destructive' });
    if (!validateMobile()) return;
    try {
      setSubmitting(true);
      const res = await api.sendOtp(mobile);
      setMode('otp');
      if (res.dev_otp) { setDevOtp(res.dev_otp); toast({ title: 'OTP sent (dev)', description: `Dev OTP: ${res.dev_otp}` }); }
      else { toast({ title: 'OTP sent', description: `OTP sent to +91 ${mobile}` }); }
    } catch (err) { toast({ title: 'Failed to send OTP', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (otp.length < 4) return toast({ title: 'Enter the OTP', variant: 'destructive' });
    try {
      setSubmitting(true);
      const res = await api.verifyOtp(mobile, otp);
      // Auth token is set as httpOnly `tgm_customer_jwt` cookie by the backend.
      // We only cache the non-sensitive user profile in localStorage for UI hydration.
      localStorage.setItem('tgmart_user', JSON.stringify(res.user));
      window.dispatchEvent(new Event('tgmart-session-change'));
      // Check if password already set
      try {
        const hp = await axios.get(`${API}/auth/has-password`, { withCredentials: true });
        if (!hp.data.has_password) {
          setPostLoginToken(res.token);
          setMode('set-pw');
          return;
        }
      } catch (err) { console.warn('LoginModal: has-password check failed (non-fatal)', err); }
      toast({ title: 'Logged in', description: 'Welcome to TelanganaMart!' });
      close();
    } catch (err) { toast({ title: 'Verification failed', description: err?.response?.data?.detail || 'Wrong OTP.', variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  const loginWithPassword = async (e) => {
    e?.preventDefault?.();
    if (!acceptedTerms) return toast({ title: 'Please accept the Terms & Privacy Policy to continue', variant: 'destructive' });
    if (!validateMobile()) return;
    if (!password) return toast({ title: 'Enter your password', variant: 'destructive' });
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/auth/login-password`, { mobile, password }, { withCredentials: true });
      // Cookie set by backend; cache profile only.
      localStorage.setItem('tgmart_user', JSON.stringify(res.data.user));
      window.dispatchEvent(new Event('tgmart-session-change'));
      toast({ title: 'Logged in', description: 'Welcome back!' });
      close();
    } catch (err) { toast({ title: 'Login failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  const forgotSendOtp = async (e) => {
    e?.preventDefault?.();
    if (!validateMobile()) return;
    try {
      setSubmitting(true);
      const res = await api.sendOtp(mobile);
      setMode('forgot-otp');
      if (res.dev_otp) { setDevOtp(res.dev_otp); toast({ title: 'OTP sent (dev)', description: `Dev OTP: ${res.dev_otp}` }); }
      else { toast({ title: 'OTP sent', description: `OTP sent to +91 ${mobile}` }); }
    } catch (err) { toast({ title: 'Failed to send OTP', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  const forgotVerifyAndSet = async (e) => {
    e?.preventDefault?.();
    if (otp.length < 4) return toast({ title: 'Enter the OTP', variant: 'destructive' });
    if (!validateNewPassword()) return;
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/auth/forgot-password/reset`, { mobile, otp, new_password: newPassword }, { withCredentials: true });
      // Cookie set by backend; cache profile only.
      localStorage.setItem('tgmart_user', JSON.stringify(res.data.user));
      window.dispatchEvent(new Event('tgmart-session-change'));
      toast({ title: 'Password reset', description: 'You are now logged in.' });
      close();
    } catch (err) { toast({ title: 'Reset failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  const setPasswordNow = async (e) => {
    e?.preventDefault?.();
    if (!validateNewPassword()) return;
    try {
      setSubmitting(true);
      await axios.post(`${API}/auth/set-password`, { new_password: newPassword }, { withCredentials: true, headers: { Authorization: `Bearer ${postLoginToken}` } });
      toast({ title: 'Password set', description: 'Next time you can sign in with mobile + password.' });
      close();
    } catch (err) { toast({ title: 'Failed to set password', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  const titleFor = {
    home: 'Sign In to TelanganaMart',
    otp: `Enter OTP sent to +91 ${mobile}`,
    'forgot-otp': 'Reset your password',
    'forgot-set': 'Reset your password',
    'set-pw': 'Set a password (optional)',
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-md" data-testid="login-modal">
        <DialogHeader>
          <DialogTitle className="text-[#2c54b2]">{titleFor[mode] || 'Sign In'}</DialogTitle>
          <DialogDescription>
            {mode === 'home' && 'Enter your mobile and choose how you want to sign in'}
            {mode === 'otp' && 'Enter the 4-6 digit OTP we just sent'}
            {mode === 'forgot-otp' && 'Enter the OTP and your new password'}
            {mode === 'set-pw' && 'Skip OTP next time — set an optional password (you can also do this later from your profile)'}
          </DialogDescription>
        </DialogHeader>

        {mode === 'home' && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="login-mobile" className="text-xs">Mobile Number</Label>
              <div className="flex gap-2 mt-1">
                <div className="flex items-center px-3 border rounded-md bg-gray-50 text-sm text-gray-700">+91</div>
                <Input id="login-mobile" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile" data-testid="login-mobile-input" />
              </div>
            </div>

            <div className="flex border-b border-gray-200">
              <button type="button" onClick={() => setTab('otp')} className={`flex-1 px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'otp' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600'}`} data-testid="login-tab-otp">
                <Smartphone className="w-4 h-4 inline mr-1" /> OTP Login
              </button>
              <button type="button" onClick={() => setTab('password')} className={`flex-1 px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'password' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600'}`} data-testid="login-tab-password">
                <Lock className="w-4 h-4 inline mr-1" /> Password
              </button>
            </div>

            {tab === 'otp' ? (
              <form onSubmit={sendOtp} className="space-y-3">
                <Button type="submit" disabled={submitting || !acceptedTerms} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="send-otp-btn">
                  <Smartphone className="w-4 h-4 mr-2" /> {submitting ? 'Sending…' : 'Send OTP'}
                </Button>
              </form>
            ) : (
              <form onSubmit={loginWithPassword} className="space-y-3">
                <div>
                  <Label htmlFor="login-password" className="text-xs">Password</Label>
                  <Input id="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" autoComplete="current-password" data-testid="login-password-input" />
                </div>
                <Button type="submit" disabled={submitting || !acceptedTerms} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="password-login-btn">
                  <Lock className="w-4 h-4 mr-2" /> {submitting ? 'Signing in…' : 'Sign In'}
                </Button>
                <button type="button" onClick={() => { setOtp(''); setNewPassword(''); setConfirmPassword(''); forgotSendOtp(); }} className="w-full text-xs text-[#2c54b2] hover:underline" data-testid="forgot-password-link">
                  Forgot password? Reset via OTP
                </button>
              </form>
            )}

            <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer select-none" data-testid="login-accept-terms-wrap">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 accent-[#ee5e23]"
                data-testid="login-accept-terms"
              />
              <span>
                I agree to TelanganaMart&apos;s{' '}
                <a href="/p/terms-and-conditions" target="_blank" rel="noreferrer" className="text-[#2c54b2] underline">Terms &amp; Conditions</a>
                {' '}and{' '}
                <a href="/p/privacy-policy" target="_blank" rel="noreferrer" className="text-[#2c54b2] underline">Privacy Policy</a>.
              </span>
            </label>
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-gray-500">or</span></div>
            </div>
            <GoogleSignInButton
              contextKey="customer-login"
              onCredential={async (credential) => {
                if (!acceptedTerms) {
                  toast({ title: 'Please accept the Terms & Privacy Policy to continue', variant: 'destructive' });
                  return;
                }
                try {
                  setSubmitting(true);
                  const res = await axios.post(`${API}/auth/google/customer`, { credential }, { withCredentials: true });
                  // Cookie set by backend; cache profile only.
                  localStorage.setItem('tgmart_user', JSON.stringify(res.data.user));
                  window.dispatchEvent(new Event('tgmart-session-change'));
                  toast({ title: res.data.new_account ? 'Account created' : 'Welcome back', description: `Signed in as ${res.data.user.email}` });
                  close();
                } catch (err) { toast({ title: 'Google sign-in failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' }); }
                finally { setSubmitting(false); }
              }}
            />
          </div>
        )}

        {mode === 'otp' && (
          <form onSubmit={verifyOtp} className="space-y-4">
            <div>
              <Label htmlFor="otp" className="text-xs">Enter OTP</Label>
              <Input id="otp" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="4-digit OTP" className="text-center tracking-widest text-lg" data-testid="otp-input" />
            </div>
            {devOtp && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Dev mode: OTP is <b className="font-mono">{devOtp}</b></div>}
            <Button type="submit" disabled={submitting} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="verify-otp-btn">
              <KeyRound className="w-4 h-4 mr-2" /> {submitting ? 'Verifying…' : 'Verify & Sign In'}
            </Button>
            <button type="button" onClick={() => { setMode('home'); setOtp(''); }} className="w-full text-xs text-[#2c54b2] hover:underline inline-flex items-center justify-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Change mobile number
            </button>
          </form>
        )}

        {mode === 'forgot-otp' && (
          <form onSubmit={forgotVerifyAndSet} className="space-y-3" data-testid="forgot-password-form">
            <div>
              <Label htmlFor="fp-otp" className="text-xs">OTP sent to +91 {mobile}</Label>
              <Input id="fp-otp" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="4-digit OTP" className="text-center tracking-widest text-lg" data-testid="fp-otp-input" />
            </div>
            {devOtp && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Dev OTP: <b className="font-mono">{devOtp}</b></div>}
            <div>
              <Label htmlFor="fp-new" className="text-xs">New Password</Label>
              <Input id="fp-new" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 8 chars, letters + numbers" autoComplete="new-password" data-testid="fp-newpass-input" />
            </div>
            <div>
              <Label htmlFor="fp-confirm" className="text-xs">Confirm Password</Label>
              <Input id="fp-confirm" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repeat password" data-testid="fp-confirm-input" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="fp-submit-btn">
              <ShieldCheck className="w-4 h-4 mr-2" /> {submitting ? 'Resetting…' : 'Reset password & sign in'}
            </Button>
            <button type="button" onClick={() => { setMode('home'); setTab('password'); setOtp(''); setNewPassword(''); setConfirmPassword(''); }} className="w-full text-xs text-[#2c54b2] hover:underline inline-flex items-center justify-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Back to sign in
            </button>
          </form>
        )}

        {mode === 'set-pw' && (
          <div className="space-y-3" data-testid="set-pw-form">
            <p className="text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded p-3">
              You&apos;re signed in! Want to skip OTP next time? Set a password now — or skip and do it later from your profile.
            </p>
            <div>
              <Label htmlFor="sp-new" className="text-xs">New Password</Label>
              <Input id="sp-new" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 8 chars, letters + numbers" autoComplete="new-password" data-testid="sp-newpass-input" />
            </div>
            <div>
              <Label htmlFor="sp-confirm" className="text-xs">Confirm Password</Label>
              <Input id="sp-confirm" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repeat password" data-testid="sp-confirm-input" />
            </div>
            <div className="flex gap-2">
              <Button onClick={setPasswordNow} disabled={submitting} className="flex-1 bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="sp-set-btn">
                <Lock className="w-4 h-4 mr-2" /> {submitting ? 'Setting…' : 'Set password'}
              </Button>
              <Button onClick={close} variant="outline" data-testid="sp-skip-btn">Skip</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LoginModal;
